import React, { useState, useEffect, useMemo } from 'react';
import { useTaskContext } from '../context/TaskContext';
import { useUserContext } from '../context/UserContext';
import { useStatusContext } from '../context/StatusContext';
import ModalWindow from '../components/ModalWindow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faUser,
  faUsers,
  faFilter,
  faCircleCheck,
  faClock,
  faMagnifyingGlass,
  faCalendarDays,
} from '@fortawesome/free-solid-svg-icons';

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d)) return '—';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const toInputDate = (d) => {
  const x = new Date(d);
  return x.toISOString().slice(0, 10);
};

const ReportsPage = () => {
  const { tasks, fetchTasks, tasksLoading } = useTaskContext();
  const { users, id: currentUserId } = useUserContext();
  const { status } = useStatusContext();

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toInputDate(d);
  });
  const [dateTo, setDateTo] = useState(() => toInputDate(new Date()));
  const [reportResult, setReportResult] = useState(null);

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Хелперы ---
  const getStatusName = (statusId) => {
    const s = status?.find((st) => st._id === statusId);
    return s ? s.name : '—';
  };
  const getStatusColor = (statusId) => {
    const s = status?.find((st) => st._id === statusId);
    return s?.color || '#7c3aed';
  };

  const doneStatusId = useMemo(() => {
    const s = status?.find((st) => st.name === 'Завершено');
    return s ? s._id : null;
  }, [status]);

  // Задачи конкретного исполнителя
  const tasksOfUser = (userId) => {
    return (tasks || []).filter((t) => {
      const list = Array.isArray(t.executors)
        ? t.executors
        : (t.executor ? [t.executor] : []);
      return list.includes(userId);
    });
  };

  const selectedUserTasks = useMemo(
    () => (selectedUserId ? tasksOfUser(selectedUserId) : []),
    [selectedUserId, tasks]
  );

  // Фильтр пользователей по поиску
  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users || []).filter((u) =>
      !q || u.name?.toLowerCase().includes(q) || u.login?.toLowerCase().includes(q)
    );
  }, [users, search]);

  // --- Отчёт ---
  const buildReport = () => {
    if (!selectedUserId) {
      setReportResult({ error: 'Сначала выберите пользователя слева' });
      return;
    }

    const from = new Date(dateFrom);
    from.setHours(0, 0, 0, 0);
    const to = new Date(dateTo);
    to.setHours(23, 59, 59, 999);

    if (from > to) {
      setReportResult({ error: 'Дата «с» больше даты «по»' });
      return;
    }

    const userTasks = tasksOfUser(selectedUserId);
    let completed = 0;
    let inProgress = 0;
    let inPeriod = 0;
    const periodDays = Math.max(1, Math.round((to - from) / 86400000) + 1);

    userTasks.forEach((t) => {
      const changes = t.statusChanges || [];
      const last = changes[changes.length - 1];
      const isDone = last && String(last.newStatus) === String(doneStatusId);

      if (isDone) {
        completed++;
        const when = Number(last.time);
        if (when >= from.getTime() && when <= to.getTime()) inPeriod++;
      } else {
        inProgress++;
      }
    });

    setReportResult({
      userId: selectedUserId,
      from: from.getTime(),
      to: to.getTime(),
      periodDays,
      total: userTasks.length,
      completed,
      inProgress,
      inPeriod,
      perDay: inPeriod > 0 ? (inPeriod / periodDays).toFixed(2) : '0.00',
    });
  };

  // --- Модалка задачи ---
  const openTask = (task) => { setSelectedTask(task); setIsModalOpen(true); };
  const closeTask = () => { setSelectedTask(null); setIsModalOpen(false); };

  const selectedUser = users?.find((u) => u._id === selectedUserId);

  return (
    <div className="crm-page">
      <div className="crm-breadcrumb">
        <span className="crm-breadcrumb__item">Рабочий стол</span>
        <span className="crm-breadcrumb__sep">›</span>
        <span className="crm-breadcrumb__item crm-breadcrumb__item--active">Отчёты</span>
      </div>

      <div className="crm-page-header">
        <h1 className="crm-page-title">
          <FontAwesomeIcon icon={faChartLine} style={{ marginRight: 10 }} />
          Отчёты
        </h1>
        <span className="reports-subtitle">
          Всего пользователей: <b>{users?.length ?? 0}</b> · задач в системе: <b>{tasks?.length ?? 0}</b>
        </span>
      </div>

      <div className="reports-layout">
        {/* --- Пользователи --- */}
        <div className="reports-panel reports-panel--users">
          <div className="reports-panel__header">
            <FontAwesomeIcon icon={faUsers} className="reports-panel__icon" />
            <span className="reports-panel__title">Пользователи</span>
            <span className="reports-panel__count">{filteredUsers.length}</span>
          </div>

          <div className="reports-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="reports-search__icon" />
            <input
              className="reports-search__input"
              placeholder="Поиск по имени или логину"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="reports-users">
            {filteredUsers.length === 0 && (
              <div className="reports-empty">Пользователи не найдены</div>
            )}
            {filteredUsers.map((u) => {
              const count = tasksOfUser(u._id).length;
              const isActive = u._id === selectedUserId;
              return (
                <button
                  key={u._id}
                  className={`reports-user ${isActive ? 'reports-user--active' : ''}`}
                  onClick={() => { setSelectedUserId(u._id); setReportResult(null); }}
                >
                  <div className="reports-user__avatar">
                    <FontAwesomeIcon icon={faUser} />
                  </div>
                  <div className="reports-user__body">
                    <span className="reports-user__name">{u.name}</span>
                    {u.login && <span className="reports-user__login">@{u.login}</span>}
                  </div>
                  <span className="reports-user__count" title="Всего задач">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* --- Правая часть --- */}
        <div className="reports-main">
          {/* Задачи выбранного пользователя */}
          <div className="reports-panel">
            <div className="reports-panel__header">
              <FontAwesomeIcon icon={faClock} className="reports-panel__icon" />
              <span className="reports-panel__title">
                {selectedUser ? `Задачи: ${selectedUser.name}` : 'Задачи пользователя'}
              </span>
              <span className="reports-panel__count">{selectedUserTasks.length}</span>
            </div>

            {!selectedUserId && (
              <div className="reports-empty">
                Выберите пользователя в списке слева
              </div>
            )}

            {selectedUserId && selectedUserTasks.length === 0 && (
              <div className="reports-empty">
                У пользователя нет назначенных задач
              </div>
            )}

            {selectedUserId && selectedUserTasks.length > 0 && (
              <div className="reports-tasks">
                {selectedUserTasks
                  .slice()
                  .sort((a, b) => {
                    const da = a.finishDate ? new Date(a.finishDate).getTime() : 0;
                    const db = b.finishDate ? new Date(b.finishDate).getTime() : 0;
                    return db - da;
                  })
                  .map((t) => (
                    <div
                      key={t._id}
                      className="reports-task"
                      onClick={() => openTask(t)}
                    >
                      <div className="reports-task__main">
                        <span className="reports-task__name">{t.name}</span>
                        {t.description && (
                          <span className="reports-task__desc">
                            {t.description.slice(0, 110)}{t.description.length > 110 ? '…' : ''}
                          </span>
                        )}
                      </div>
                      <div className="reports-task__meta">
                        <span
                          className="reports-task__status"
                          style={{
                            background: `${getStatusColor(t.status)}22`,
                            color: getStatusColor(t.status),
                            borderColor: `${getStatusColor(t.status)}55`,
                          }}
                        >
                          {getStatusName(t.status)}
                        </span>
                        <span className="reports-task__date">
                          {t.finishDate ? formatDate(t.finishDate) : 'Без срока'}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Форма отчёта */}
          <div className="reports-panel">
            <div className="reports-panel__header">
              <FontAwesomeIcon icon={faFilter} className="reports-panel__icon" />
              <span className="reports-panel__title">Отчёт за период</span>
            </div>

            <div className="reports-form">
              <label className="reports-form__field">
                <span className="reports-form__label">С даты</span>
                <input
                  type="date"
                  className="reports-form__input"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </label>

              <label className="reports-form__field">
                <span className="reports-form__label">По дату</span>
                <input
                  type="date"
                  className="reports-form__input"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </label>

              <button className="reports-form__submit" onClick={buildReport}>
                <FontAwesomeIcon icon={faCalendarDays} />
                <span>Показать</span>
              </button>
            </div>

            {reportResult?.error && (
              <div className="reports-error">{reportResult.error}</div>
            )}

            {reportResult && !reportResult.error && (
              <div className="reports-result">
                <div className="reports-result__title">
                  Пользователь: <b>{selectedUser?.name}</b>
                  <br />
                  Период: {formatDate(reportResult.from)} — {formatDate(reportResult.to)}
                  <span className="reports-result__days">({reportResult.periodDays} дн.)</span>
                </div>

                <div className="reports-result__grid">
                  <div className="reports-result__stat reports-result__stat--blue">
                    <div className="reports-result__value">{reportResult.total}</div>
                    <div className="reports-result__label">Всего задач</div>
                  </div>
                  <div className="reports-result__stat reports-result__stat--green">
                    <div className="reports-result__value">{reportResult.completed}</div>
                    <div className="reports-result__label">
                      <FontAwesomeIcon icon={faCircleCheck} /> Завершено (всего)
                    </div>
                  </div>
                  <div className="reports-result__stat reports-result__stat--amber">
                    <div className="reports-result__value">{reportResult.inProgress}</div>
                    <div className="reports-result__label">В работе</div>
                  </div>
                  <div className="reports-result__stat reports-result__stat--violet">
                    <div className="reports-result__value">{reportResult.inPeriod}</div>
                    <div className="reports-result__label">
                      Завершено за период
                    </div>
                  </div>
                  <div className="reports-result__stat reports-result__stat--teal">
                    <div className="reports-result__value">{reportResult.perDay}</div>
                    <div className="reports-result__label">Задач в день</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && <ModalWindow task={selectedTask} onClose={closeTask} />}
    </div>
  );
};

export default ReportsPage;